import React, {
  createContext, useContext, useState, useEffect, useCallback
} from 'react';
import { SERVER_URL } from '../constants/server-type';


// keep your existing api helpers if you have them
import {
  refreshAccessToken, // must refresh the token and (ideally) store it
} from '../api/api';

// ---------- helpers ----------
function parseToken(t) {
  try {
    const [, p] = String(t || '').split('.');
    if (!p) return null;
    // handle URL-safe base64 just in case
    const b64 = p.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(b64))));
  } catch {
    return null;
  }
}

function decodeJwt(t) {
  try {
    const [, p] = String(t || '').split('.');
    return p ? JSON.parse(atob(p)) : null;
  } catch {
    return null;
  }
}

const AuthContext = createContext();

// ===================================================================
// Provider
// ===================================================================
export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const issuperadmin = !!user?.globals?.includes('superadmin');

  // single path to update user state from a token
  const setFromToken = useCallback((token) => {
    if (!token) { setUser(null); return; }

    const payload = parseToken(token);
    if (!payload) {
      localStorage.removeItem('accessToken');
      setUser(null);
      return;
    }

    const roles   = Array.isArray(payload.roles)   ? payload.roles.map(r => String(r).toLowerCase()) : [];
    const globals = Array.isArray(payload.globals) ? payload.globals.map(r => String(r).toLowerCase()) : [];

    setUser({
      id: payload.sub || payload.id || null,
      firstName: payload.firstName || '',
      lastName: payload.lastName || '',
      orgId: payload.orgId || null,
      roles,
      globals,
      raw: payload,
    });

    // Seed activeOrgId here while we have the payload in scope
    if (payload.orgId && !localStorage.getItem('activeOrgId')) {
      localStorage.setItem('activeOrgId', String(payload.orgId));
    }
  }, []);

  // Back-compat alias — some older code still calls setToken(...)
  const setToken = (t) => setFromToken(t);

  // On mount: read existing token from storage
  useEffect(() => { setFromToken(localStorage.getItem('accessToken')); }, [setFromToken]);

  // Keep multiple tabs in sync
  useEffect(() => {
    const onStorage = () => setFromToken(localStorage.getItem('accessToken'));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [setFromToken]);

  // Proactive refresh scheduler (refresh 60s before exp)
  useEffect(() => {
    let timer;
    const schedule = () => {
      const t = localStorage.getItem('accessToken');
      const p = decodeJwt(t);
      if (!p?.exp) return;

      const msToExp = p.exp * 1000 - Date.now();
      const lead = 60 * 1000; // refresh 60s early
      const wait = Math.max(5_000, msToExp - lead);

      timer = setTimeout(async () => {

        try {

          await refreshAccessToken();

          setFromToken(
            localStorage.getItem(
              'accessToken'
            )
          );

          schedule();

        } catch (err) {

          console.warn(
            'Refresh failed',
            err
          );

          localStorage.removeItem(
            'accessToken'
          );

          setUser(null);

          window.location.href =
            '/login';
        }

      }, wait);
    };

    schedule();
    return () => timer && clearTimeout(timer);
  }, [setFromToken]);

  // ---------- auth actions ----------
  const login = async (email, password) => {
    const resp = await fetch(`${SERVER_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // if you use cookies for refresh
      body: JSON.stringify({ email, password }),
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => '');
      throw new Error(txt || 'Login failed');
    }

    const data = await resp.json(); // { accessToken, refreshToken?, orgId? }
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
      setFromToken(data.accessToken);
    }
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
    }
    // Also seed activeOrgId from response if present
    if (data.orgId && !localStorage.getItem('activeOrgId')) {
      localStorage.setItem('activeOrgId', String(data.orgId));
    }
    return true;
  };

  const logout = async () => {
    try {
      await fetch(`${SERVER_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    localStorage.removeItem('accessToken');
    // optional: localStorage.removeItem('refreshToken');
    // optional: localStorage.removeItem('activeOrgId');
    setUser(null);
  };

  const loading = user === undefined;

  return (
    <AuthContext.Provider value={{ user, loading, issuperadmin, login, logout, setFromToken, setToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
